import React from 'react';
import {
  useReactFlow,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  EdgeProps,
} from '@xyflow/react';

/**
 * DeletableEdge — a bezier edge with an inline × button to remove the connection.
 * Optionally renders a label above the button (e.g. "Yes" / "No" for conditional branches).
 *
 * Register as edgeType `deletable`:
 *   const edgeTypes = { deletable: DeletableEdge };
 */
const DeletableEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
  label,
  data,
}) => {
  const { setEdges } = useReactFlow();
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={
          data?.is_back_edge
            ? { ...style, strokeDasharray: '6 3', stroke: '#8b5cf6' }
            : style
        }
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
          className="nodrag nopan"
        >
          {label && (
            <span
              style={{
                background: 'var(--mg-bg-elevated)',
                border: '1px solid var(--mg-border)',
                borderRadius: 4,
                padding: '1px 6px',
                fontSize: 11,
                color: 'var(--mg-text-secondary)',
                fontWeight: 600,
              }}
            >
              {label as string}
            </span>
          )}
          <button
            onClick={() => setEdges((eds) => eds.filter((e) => e.id !== id))}
            title="Remove connection"
            style={{
              width: 18,
              height: 18,
              background: 'var(--mg-bg-elevated)',
              border: '1px solid var(--mg-border)',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              color: 'var(--mg-text-secondary)',
              padding: 0,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

export default DeletableEdge;
